import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { onToast } from "@components/Toast";

import type { TGetBroadcastStatusResponse } from "@api/broadcast";

import { TChatStore } from "@storage/global/useChatStore";
import { type TToysStore, useToysStore } from "@storage/global/useToysStore";
import type { TWebSocketStore } from "@storage/global/useWebSocketStore";
import { getBroadcastStatusData } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { resetToysSession } from "@storage/queries/toys/resetToysSession";
import { TUserAuthData } from "@storage/session/types";
import {
	type TToysAvailabilityStorageValue,
	currentRoomChatIdStorage,
	isLoggingOutStorage,
	tipMenuEnabledStorage,
	toysAvailabilityStorage,
} from "@storage/sessionStorage";

import { playAudio } from "@lib/audio/playAudio";
import { Lovense } from "@lib/lovence";

import { moneyAnimation } from "@utils/moneyAnimation";
import { scrollIntoElement } from "@utils/scrollIntoElement";
import { showNotificationIfOffline } from "@utils/showNotificationIfOffline";

import { QUERY_KEYS } from "@configs/api";
import { toyMessageDescriptionMap, toyMessageTitleMap } from "@configs/toys";

import { reconnectWs } from "./helpers";
import {
	ECause,
	type TBroadcastGoalEndedResponse,
	type TBroadcastGoalStartedResponse,
	type TBroadcastPrivateModeEnabledForModel,
	type TBroadcastStreamStatusChanged,
	type TMessageResponse,
	type TPrivateRequestRefusedByBilling,
	type TPrivateRequestRefusedByModel,
	type TRoomChatUsersUpdate,
	type TSoundNotificationTriggered,
	type TToyControlSessionChangedResponse,
} from "./types";

type TWebSocketStoreTypesCombine = TWebSocketStore & TChatStore & TToysStore;

export const webSocketHandleEvents = (
	storeOrDispatch: TWebSocketStoreTypesCombine,
	queryClient: QueryClient,
	currentAccount: TUserAuthData,
	ws: WebSocket,
) => ({
	broadcastBalanceUpdated: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MODEL_BALANCE] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOKEN_PAST_12H] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
	},
	broadcastStreamStatusChanged: ({ payload }: { payload: TBroadcastStreamStatusChanged }) => {
		const getBool2DStatus = payload["2dStreamStatus"] === "online";
		const getBool3DStatus = payload["3dStreamStatus"] === "online";
		const broadcastStatusData = queryClient.getQueryData<TGetBroadcastStatusResponse>([
			QUERY_KEYS.BROADCAST_STATUS_DATA,
		]);

		showNotificationIfOffline(
			getBool2DStatus,
			getBool3DStatus,
			broadcastStatusData?.is2DStreamActive,
			broadcastStatusData?.is3DStreamActive,
		);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BROADCAST_SETTINGS] });
	},
	broadcastStarted: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

		const TIP_MENU_ENABLED = tipMenuEnabledStorage.getItemAsJson<boolean>();

		if (!TIP_MENU_ENABLED) {
			const systemMessage = {
				type: "systemMessage",
				fromClientNickname: "System Message",
				timestamp: Date.now(),
				payload: {
					userType: "systemMessage",
				},
			} as TMessageResponse;
			storeOrDispatch.setMessageList(systemMessage);
			scrollIntoElement("scroller");
		}
	},
	broadcastStoppedByModel: () => {
		const isLoggingOut = isLoggingOutStorage.getItem();

		if (!isLoggingOut) {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

			storeOrDispatch.clearMessageList();

			currentRoomChatIdStorage.removeItem();
			ws.send(JSON.stringify(reconnectWs(currentAccount.roomChatId || "")));
		}
	},
	broadcastAwayModeEnabled: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
	},
	broadcastAwayModeDisabled: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
	},
	broadcastStoppedByTechnicalReasons: () => {
		onToast({
			message: "Error",
			description: "The broadcast is currently Offline due to technical issues",
		});

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
	},
	broadcastPrivateModeRequestCreatedByClient: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });

		playAudio("broadcastPrivateModeRequestCreatedByClient", null);
	},
	broadcastPrivateModeEnabled: async () => {
		const response = await getBroadcastStatusData();

		if (!response) return;

		queryClient.setQueryData([QUERY_KEYS.BROADCAST_STATUS_DATA], response);
		storeOrDispatch.clearMessageList();

		const currentChat = response.privateInfo?.privateRoomChatId
			? response.privateInfo?.privateRoomChatId
			: currentAccount.roomChatId;

		currentRoomChatIdStorage.setItem(currentChat);
		ws.send(JSON.stringify(reconnectWs(currentChat)));
	},
	broadcastPrivateModeEnabledForModel: ({ payload }: { payload: TBroadcastPrivateModeEnabledForModel }) => {
		playAudio("broadcastPrivateModeEnabledForModel", null);
		toast.dismiss(payload.id);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	broadcastPrivateModeRequestRefusedByModel: ({ payload }: { payload: TPrivateRequestRefusedByModel }) => {
		playAudio("broadcastPrivateModeRequestRefusedByModel", null);
		toast.dismiss(payload.privateRequestId);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	broadcastPrivateModeRequestRefusedByClient: ({ payload }: { payload: TPrivateRequestRefusedByModel }) => {
		playAudio("broadcastPrivateModeRequestRefusedByClient", null);
		toast.dismiss(payload.privateRequestId);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	broadcastPrivateModeRequestRefusedByBilling: ({ payload }: { payload: TPrivateRequestRefusedByBilling }) => {
		onToast({
			message: "Attention",
			description: `Private show request from ${payload.clientNickname} was denied due to insufficient funds`,
		});

		playAudio("broadcastPrivateModeRequestRefusedByBilling", null);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	broadcastPrivateModeDisabledByModel: ({ payload }: { payload: { roomChatId: string } }) => {
		playAudio("broadcastPrivateModeDisabledByModel", null);

		storeOrDispatch.setIsOpenToyContainer(false);
		toysAvailabilityStorage.removeItem();

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

		storeOrDispatch.clearMessageList();

		currentRoomChatIdStorage.removeItem();
		ws.send(JSON.stringify(reconnectWs(payload.roomChatId)));
	},
	broadcastPrivateModeDisabledByClient: ({ payload }: { payload: { roomChatId: string } }) => {
		playAudio("broadcastPrivateModeDisabledByClient", null);

		storeOrDispatch.setIsOpenToyContainer(false);
		toysAvailabilityStorage.removeItem();

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

		storeOrDispatch.clearMessageList();

		currentRoomChatIdStorage.removeItem();
		ws.send(JSON.stringify(reconnectWs(payload.roomChatId)));
	},
	broadcastPrivateModeDisabledByBilling: ({ payload }: { payload: { roomChatId: string } }) => {
		playAudio("broadcastPrivateModeDisabledByBilling", null);
		storeOrDispatch.setIsOpenToyContainer(false);
		toysAvailabilityStorage.removeItem();

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

		storeOrDispatch.clearMessageList();

		currentRoomChatIdStorage.removeItem();
		ws.send(JSON.stringify(reconnectWs(payload.roomChatId)));
	},
	broadcastPrivateRequestCanceledDueToMaintenanceReasons: () => {
		storeOrDispatch.setIsOpenToyContainer(false);
		toysAvailabilityStorage.removeItem();
		playAudio("broadcastPrivateRequestCanceledDueToMaintenanceReasons", null);
	},
	broadcastPrivateModeCanceledDueToTechnicalReasons: ({ payload }: { payload: { roomChatId: string } }) => {
		playAudio("broadcastPrivateModeCanceledDueToTechnicalReasons", null);

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });

		storeOrDispatch.setIsOpenToyContainer(false);
		toysAvailabilityStorage.removeItem();
		storeOrDispatch.clearMessageList();

		currentRoomChatIdStorage.removeItem();
		ws.send(JSON.stringify(reconnectWs(payload.roomChatId)));
	},
	broadcastPrivateModeRequestExpired: ({ payload }: { payload: TPrivateRequestRefusedByModel }) => {
		toast.dismiss(payload.privateRequestId);

		onToast({
			message: "Attention",
			description: "Private show request has expired",
		});

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	broadcastViewersCount: ({ payload }: { payload: { count: number } }) => {
		storeOrDispatch.updateViewersCount(payload.count);
	},
	roomChatUsersUpdate: ({ payload }: { payload: TRoomChatUsersUpdate }) => {
		storeOrDispatch.updateUsersData(payload);
	},
	blackListBanned: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BLACK_LIST_DATA] });
	},
	broadcastSpyViewersCountChanged: ({ payload }: { payload: { count: number } }) => {
		storeOrDispatch.updateSpyCount(payload.count);
	},
	tipsModelSpecialAnimationTriggered: () => {
		moneyAnimation();
	},
	soundNotificationTriggered: ({ payload }: { payload: TSoundNotificationTriggered }) => {
		playAudio(payload.eventType, payload.amount);
	},
	broadcastSettingsChanged: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRIVATE_REQUEST_DATA] });
	},
	lovenseDeviceTriggered: (message: {
		payload: TSoundNotificationTriggered;
		fromClientNickname: string;
		from: { nickname: string };
	}) => {
		Lovense.receiveTip(message.payload.amount, message?.fromClientNickname || message?.from?.nickname || "");
	},
	modelStudioPaymentSettingsChanged: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYMENT_METHODS_STATE] });
	},
	verificationStatusChanged: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.VERIFICATION_INFO] });
	},
	message: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		scrollIntoElement("scroller");
	},

	complimentSent: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		scrollIntoElement("scroller");
	},
	tipsSent: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		scrollIntoElement("scroller");
	},
	toysMenuItemPurchased: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		scrollIntoElement("scroller");
	},
	tipMenuPurchased: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		scrollIntoElement("scroller");
	},
	supportModelMessage: (message: TMessageResponse) => {
		storeOrDispatch.setMessageList(message);

		const currentTimestamp = Math.floor(Date.now() / 1000);
		const messageTimestamp = message.timestamp;

		if (currentTimestamp === messageTimestamp) {
			storeOrDispatch.setIsNewSystemMessage(true);
		}
	},
	deleteAllUserMessages: ({ payload }: { payload: { userId: string } }) => {
		storeOrDispatch.deleteAllUserMessages(payload.userId);
	},
	tipMenuChanged: () => {
		// tipMenuEnabledStorage.setItem(payload.tipMenuEnabled.toString());

		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TIP_MENU_DATA] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TIP_MENU_STATUS] });
	},
	broadcastGoalsChanged: () => {
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_GOAL_LIST] });
	},
	broadcastGoalStarted: ({ payload, type, timestamp }: TBroadcastGoalStartedResponse) => {
		const goalMessage = {
			type: type,
			fromClientNickname: "System Message",
			timestamp: timestamp,
			payload: {
				userType: "systemMessage",
				title: payload.goal.title,
			},
		} as TMessageResponse;

		storeOrDispatch.setMessageList(goalMessage);

		scrollIntoElement("scroller");
	},
	broadcastGoalEnded: ({ payload, type, timestamp }: TBroadcastGoalEndedResponse) => {
		if (payload.reason === "finished") {
			const goalMessage = {
				type: type,
				fromClientNickname: "System Message",
				timestamp: timestamp,
				payload: {
					userType: "systemMessage",
					title: payload.goal.title,
				},
			} as TMessageResponse;

			storeOrDispatch.setMessageList(goalMessage);

			scrollIntoElement("scroller");
		}

		if (("bof" in payload && "eof" in payload) || payload.reason === "canceled") return;

		playAudio("broadcastGoalEnded", null);
	},
	toyControlSessionChanged: ({ payload, type, timestamp }: TToyControlSessionChangedResponse) => {
		if (payload.cause === ECause.CLOSED) {
			resetToysSession(queryClient);
			return;
		}

		const privateShowId = `${payload.broadcastId}:${payload.privateRequestId}`;
		if (payload.cause === ECause.STOPPED && useToysStore.getState().activePrivateShowId !== privateShowId) {
			return;
		}

		/**
		 * Additional prevention logic to reset session if created event arrives before the previous session is closed.
		 */
		if (payload.cause === ECause.CREATED) {
			resetToysSession(queryClient);
		}

		storeOrDispatch.triggerToysSession(privateShowId);

		/**
		 *  Restart even the initial fetch if a newer event arrives before it completes.
		 */
		queryClient.cancelQueries({ queryKey: [QUERY_KEYS.TOYS_SESSION] });
		queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOYS_SESSION], refetchType: "all" });

		const toyMessage = {
			type,
			fromClientNickname: "System Message",
			timestamp,
			payload: {
				userType: "systemMessage",
				title: toyMessageTitleMap[payload.cause as keyof typeof toyMessageTitleMap],
				description: toyMessageDescriptionMap[payload.cause as keyof typeof toyMessageDescriptionMap],
			},
		} as TMessageResponse;

		switch (payload.cause) {
			case ECause.CREATED:
				storeOrDispatch.setMessageList(toyMessage);
				scrollIntoElement("scroller");

				break;
			case ECause.OFFERED: {
				const toysAvailability = toysAvailabilityStorage.getItemAsJson<TToysAvailabilityStorageValue>();

				toysAvailabilityStorage.setItemAsJson<TToysAvailabilityStorageValue>({
					privateShowId,
					isAvailable: toysAvailability?.privateShowId === privateShowId && toysAvailability.isAvailable,
					isProposalPending: true,
				});
				storeOrDispatch.setIsOpenControlProposalPopUp(true);
				break;
			}
			case ECause.ACCEPTED:
				toysAvailabilityStorage.setItemAsJson({
					privateShowId,
					isAvailable: true,
				});
				storeOrDispatch.setMessageList(toyMessage);
				scrollIntoElement("scroller");

				storeOrDispatch.setIsOpenToyContainer(true);
				storeOrDispatch.setIsOpenControlProposalPopUp(false);

				break;
			case ECause.STOPPED:
				break;
			case ECause.REVOKED:
				storeOrDispatch.setMessageList(toyMessage);
				scrollIntoElement("scroller");

				break;
			case ECause.CANCELED:
				storeOrDispatch.setIsOpenControlProposalPopUp(false);

				break;
			case ECause.DECLINED:
				break;
			case ECause.EXPIRED:
				break;
			case ECause.OFFLINE:
				break;
		}
	},
	DEFAULT: () => {},
});
